import { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useCoupons } from '../../hooks/useCoupons';
import CouponCard from '../../components/coupon/CouponCard';
import RedemptionModal from '../../components/coupon/RedemptionModal';
import CategoryFilter from '../../components/coupon/CategoryFilter';
import { Pagination, Spinner } from '../../components/common/UI';
import { US_STATES } from '../../utils/helpers';

const SORT_OPTIONS = [
  { value: 'priority', label: 'Recommended', icon: '✨' },
  { value: 'newest',   label: 'Newest',      icon: '🆕' },
  { value: 'expiring', label: 'Ending soon', icon: '🔥' },
  { value: 'discount', label: 'Biggest discount', icon: '💥' },
];

const DISCOUNT_TYPES = [
  { value: '',        label: 'All types' },
  { value: 'percent', label: '% Off' },
  { value: 'fixed',   label: '$ Off' },
  { value: 'freebie', label: 'Free item' },
  { value: 'bogo',    label: 'BOGO' },
];

export default function CouponsPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const [redeemCoupon, setRedeemCoupon] = useState(null);
  const [page, setPage] = useState(1);

  const filters = {
    category:      searchParams.get('category') || '',
    search:        searchParams.get('search')   || '',
    state:         searchParams.get('state')    || '',
    discount_type: searchParams.get('discount_type') || '',
    featured:      searchParams.get('featured') || '',
    sort:          searchParams.get('sort')     || 'priority',
    is_online:     searchParams.get('online') === 'true' ? true : undefined,
    is_in_store:   searchParams.get('instore') === 'true' ? true : undefined,
    limit: 24, page,
  };

  const { coupons, meta, loading, error } = useCoupons(filters);

  const setFilter = (key, value) => {
    setPage(1);
    const next = new URLSearchParams(searchParams);
    if (value) next.set(key, value); else next.delete(key);
    setSearchParams(next);
  };

  const [localSearch, setLocalSearch] = useState(searchParams.get('search') || '');
  useEffect(() => { setLocalSearch(searchParams.get('search') || ''); }, [searchParams]);

  const handleSearchSubmit = (e) => {
    e.preventDefault();
    setFilter('search', localSearch);
  };

  const activeFilterCount = [filters.category, filters.search, filters.state, filters.discount_type, filters.is_online, filters.is_in_store].filter(Boolean).length;

  const pageTitle = filters.search ? `Results for "${filters.search}"`
                  : filters.category ? `${filters.category.replace(/-/g, ' ').replace(/\b\w/g, c => c.toUpperCase())} deals`
                  : filters.featured === 'true' ? 'Featured deals'
                  : 'All deals';

  return (
    <div style={{ background: '#F7F8FC', minHeight: '80vh' }}>
      {/* Banner */}
      <div style={{ background: 'var(--hero-gradient)', color: '#fff', padding: '48px 0 36px', position: 'relative', overflow: 'hidden' }}>
        <div style={{ position: 'absolute', top: '-30%', right: '-10%', width: 420, height: 420, borderRadius: '50%', background: 'radial-gradient(circle, rgba(217,70,239,0.3) 0%, transparent 70%)', pointerEvents: 'none' }} />
        <div className="container" style={{ position: 'relative' }}>
          <h1 style={{ fontSize: 'clamp(28px, 3.8vw, 44px)', fontWeight: 800, letterSpacing: '-0.03em', lineHeight: 1.15, marginBottom: 10 }}>{pageTitle}</h1>
          <p style={{ fontSize: 15, opacity: 0.85, marginBottom: 24 }}>
            {loading ? 'Finding your perks…' : `${meta.total?.toLocaleString() ?? 0} verified deals available right now`}
          </p>

          {/* Search bar */}
          <form onSubmit={handleSearchSubmit} style={{ display: 'flex', background: '#fff', borderRadius: 999, padding: 6, boxShadow: '0 8px 24px rgba(14,15,46,0.2)', maxWidth: 620 }}>
            <span style={{ display: 'flex', alignItems: 'center', padding: '0 8px 0 18px', color: '#8B94A8', fontSize: 18 }}>🔍</span>
            <input
              value={localSearch}
              onChange={e => setLocalSearch(e.target.value)}
              placeholder="Search brands, categories, or deals…"
              style={{ flex: 1, padding: '12px 8px', border: 'none', outline: 'none', fontSize: 15, color: '#0F1225', background: 'transparent', minWidth: 0 }}
            />
            <button type="submit" className="btn btn-primary" style={{ borderRadius: 999, padding: '10px 22px', fontSize: 14 }}>Search</button>
          </form>
        </div>
      </div>

      <div className="container" style={{ padding: '28px 20px 64px' }}>
        {/* Category pills */}
        <div style={{ marginBottom: 20 }}>
          <CategoryFilter selected={filters.category || null} onChange={v => setFilter('category', v)} />
        </div>

        {/* Filters row */}
        <div style={{ background: '#fff', borderRadius: 14, padding: '14px 18px', marginBottom: 24, border: '1px solid #E4E7F0', display: 'flex', gap: 10, flexWrap: 'wrap', alignItems: 'center' }}>
          <FilterSelect
            value={filters.sort} onChange={v => setFilter('sort', v)} icon="↕"
            options={SORT_OPTIONS.map(o => ({ value: o.value, label: `${o.icon} ${o.label}` }))}
          />
          <FilterSelect
            value={filters.state} onChange={v => setFilter('state', v)} icon="📍"
            options={[{ value: '', label: 'All states' }, ...US_STATES.map(s => ({ value: s.code, label: s.name }))]}
          />
          <FilterSelect
            value={filters.discount_type} onChange={v => setFilter('discount_type', v)} icon="💸"
            options={DISCOUNT_TYPES}
          />

          <Toggle checked={searchParams.get('online') === 'true'} onChange={v => setFilter('online', v ? 'true' : '')} label="🌐 Online" />
          <Toggle checked={searchParams.get('instore') === 'true'} onChange={v => setFilter('instore', v ? 'true' : '')} label="📍 In-store" />

          <div style={{ flex: 1 }} />

          {activeFilterCount > 0 && (
            <button
              onClick={() => { setSearchParams({}); setLocalSearch(''); }}
              style={{ padding: '8px 14px', background: 'transparent', border: '1.5px solid #E4E7F0', borderRadius: 999, cursor: 'pointer', fontSize: 13, fontWeight: 600, color: '#6B2FD9', fontFamily: 'inherit' }}
            >
              Clear all ({activeFilterCount}) ×
            </button>
          )}
        </div>

        {/* Results */}
        {loading ? (
          <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', padding: '80px 0', gap: 14 }}>
            <Spinner size={36} />
            <p style={{ fontSize: 14, color: '#8B94A8' }}>Loading deals…</p>
          </div>
        ) : error ? (
          <div className="alert alert-danger">{error}</div>
        ) : coupons.length === 0 ? (
          <div style={{ background: '#fff', borderRadius: 16, padding: '72px 20px', textAlign: 'center', border: '1px solid #E4E7F0' }}>
            <div style={{ fontSize: 56, marginBottom: 16 }}>🔍</div>
            <h3 style={{ fontSize: 20, fontWeight: 800, letterSpacing: '-0.02em', marginBottom: 8 }}>No deals match those filters</h3>
            <p style={{ fontSize: 14, color: '#4A5568', marginBottom: 24, maxWidth: 380, margin: '0 auto 24px' }}>
              Try removing a filter, browsing a different category, or checking back soon — we add new deals daily.
            </p>
            <button onClick={() => { setSearchParams({}); setLocalSearch(''); }} className="btn btn-primary">
              Clear filters
            </button>
          </div>
        ) : (
          <>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))', gap: 20, marginBottom: 40 }}>
              {coupons.map(c => <CouponCard key={c.id} coupon={c} onRedeem={setRedeemCoupon} />)}
            </div>
            <Pagination meta={meta} onPageChange={p => { setPage(p); window.scrollTo({ top: 0, behavior: 'smooth' }); }} />
          </>
        )}
      </div>

      {redeemCoupon && <RedemptionModal coupon={redeemCoupon} onClose={() => setRedeemCoupon(null)} />}
    </div>
  );
}

function FilterSelect({ value, onChange, options, icon }) {
  return (
    <div style={{ position: 'relative', display: 'inline-flex', alignItems: 'center' }}>
      {icon && <span style={{ position: 'absolute', left: 12, fontSize: 13, color: '#8B94A8', pointerEvents: 'none' }}>{icon}</span>}
      <select
        value={value}
        onChange={e => onChange(e.target.value)}
        style={{
          appearance: 'none',
          padding: icon ? '9px 34px 9px 34px' : '9px 34px 9px 14px',
          borderRadius: 999, border: '1.5px solid #E4E7F0',
          background: '#fff', color: '#0F1225',
          fontSize: 13, fontWeight: 600, cursor: 'pointer',
          fontFamily: 'inherit', outline: 'none',
          backgroundImage: "url(\"data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='10' height='10' viewBox='0 0 12 12'%3E%3Cpath fill='%236B2FD9' d='M6 8L1 3h10z'/%3E%3C/svg%3E\")",
          backgroundRepeat: 'no-repeat', backgroundPosition: 'right 12px center',
          transition: 'all 0.15s',
        }}
      >
        {options.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
      </select>
    </div>
  );
}

function Toggle({ checked, onChange, label }) {
  return (
    <label style={{
      display: 'inline-flex', alignItems: 'center', gap: 8,
      padding: '8px 14px', borderRadius: 999,
      border: `1.5px solid ${checked ? 'transparent' : '#E4E7F0'}`,
      background: checked ? 'var(--brand-gradient)' : '#fff',
      color: checked ? '#fff' : '#4A5568',
      fontSize: 13, fontWeight: 600, cursor: 'pointer',
      boxShadow: checked ? 'var(--shadow-glow)' : 'none',
      transition: 'all 0.15s',
    }}>
      <input type="checkbox" checked={checked} onChange={e => onChange(e.target.checked)} style={{ display: 'none' }} />
      {label}
    </label>
  );
}
